import { Trans, msg, t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import cx from "classnames";
import { ReactNode, useMemo } from "react";
import useSWR from "swr";
import type { Address } from "viem";
import { parseUnits } from "viem";

import { USD_DECIMALS } from "config/factors";
import { useShowDebugValues } from "context/SyntheticsStateContext/hooks/settingsHooks";
import { PnlSummaryPoint, usePnlSummaryData } from "domain/synthetics/accountStats";
import { fetchLeaderboard } from "lib/apis/leaderboard/requests";
import { getReferralsSummary } from "lib/apis/referral/requests";
import { formatUsd } from "lib/numbers";
import { getPositiveOrNegativeClass } from "lib/utils";

import { CardRow } from "components/CardRow/CardRow";
import StatsTooltipRow from "components/StatsTooltip/StatsTooltipRow";
import TooltipWithPortal from "components/Tooltip/TooltipWithPortal";

import { GeneralPerformanceDetailsDebugTooltip } from "./generalPerformanceDetailsDebug";

const bucketLabelMap = {
  today: msg`Today`,
  yesterday: msg`Yesterday`,
  week: msg`Last 7d`,
  month: msg`Last 30d`,
  year: msg`This Year`,
  all: msg`All Time`,
};

export function GeneralPerformanceDetails({ chainId, account }: { chainId: number; account: Address }) {
  const normalizedAccount = account?.toLowerCase();
  const { data, error: _error, loading } = usePnlSummaryData(chainId, account);
  const showDebugValues = useShowDebugValues();

  const row = useMemo(() => {
    if (data?.length && !loading) {
      return (
        data.find((r) => r.bucketLabel?.toLowerCase() === "all") ??
        data.find((r) => r.bucketLabel?.toLowerCase().includes("all")) ??
        data[0]
      );
    }
    return undefined;
  }, [data, loading]);

  const { data: leaderboardData } = useSWR(
    normalizedAccount ? ["leaderboardRank", chainId, normalizedAccount] : null,
    () => fetchLeaderboard(chainId, { limit: 100 })
  );

  const leaderboardRank = useMemo(() => {
    if (!leaderboardData?.items) return undefined;
    const idx = leaderboardData.items.findIndex((item) => item.account.toLowerCase() === normalizedAccount);
    return idx >= 0 ? idx + 1 : undefined;
  }, [leaderboardData, normalizedAccount]);

  const { data: referralData, isLoading: isReferralLoading } = useSWR(
    normalizedAccount ? ["referralSummary", chainId, normalizedAccount] : null,
    () => getReferralsSummary(normalizedAccount!, { chainId })
  );

  const referralRewards = parseUsdString(referralData?.affiliateTotalStats?.totalRebateUsd);
  const hasReferralStats = Boolean(referralData?.affiliateTotalStats);

  const placeholderValue = <span className="text-gray-lighter">—</span>;

  return (
    <div className="size-full overflow-hidden rounded-8 bg-gray-darker p-24 pb-32 pt-12 sm:px-32 sm:py-18 sm:pb-36">
      <div className="mb-24 text-14 font-medium leading-[1] text-white sm:mb-32 sm:text-[20px]">
        <Trans>Portfolio Overview</Trans>
      </div>

      <div className="flex flex-col gap-24 sm:gap-32">
        <CustomCardRow
          label={<Trans>PNL</Trans>}
          value={
            row ? (
              <TooltipWithPortal
                disableHandleStyle
                tooltipClassName="cursor-help *:cursor-auto"
                className={cx("cursor-help", {
                  "text-primary-alpha-darker": row.pnlUsd > 0n,
                  "text-red-alpha-100": row.pnlUsd < 0n,
                })}
                content={
                  showDebugValues ? (
                    <GeneralPerformanceDetailsDebugTooltip row={row} />
                  ) : (
                    <>
                      <StatsTooltipRow
                        label={t`Realized PnL`}
                        showDollar={false}
                        textClassName={getPositiveOrNegativeClass(row.realizedPnlUsd)}
                        value={formatUsd(row.realizedPnlUsd)}
                      />
                      <StatsTooltipRow
                        label={t`Unrealized PnL`}
                        showDollar={false}
                        textClassName={getPositiveOrNegativeClass(row.unrealizedPnlUsd)}
                        value={formatUsd(row.unrealizedPnlUsd)}
                      />
                      <StatsTooltipRow
                        label={t`Start Unrealized PnL`}
                        showDollar={false}
                        textClassName={getPositiveOrNegativeClass(row.startUnrealizedPnlUsd)}
                        value={formatUsd(row.startUnrealizedPnlUsd)}
                      />
                    </>
                  )
                }
              >
                {formatUsd(row.pnlUsd, { displayPlus: true })}
              </TooltipWithPortal>
            ) : (
              placeholderValue
            )
          }
        />
        <CustomCardRow
          label={<Trans>Volume</Trans>}
          value={row ? formatUsd(row.volume, { maxThreshold: null, displayPlus: true }) : placeholderValue}
        />
        <CustomCardRow
          label={<Trans>Leaderboard Rank</Trans>}
          value={leaderboardRank ? `#${leaderboardRank}` : placeholderValue}
        />
        <CustomCardRow
          label={<Trans>Referral Rewards</Trans>}
          value={!isReferralLoading && hasReferralStats ? formatUsd(referralRewards) : placeholderValue}
        />
      </div>
    </div>
  );
}

function parseUsdString(value: string | undefined | null): bigint {
  if (!value) return 0n;
  const normalized = String(value).trim();
  if (!normalized || normalized === "0") return 0n;

  if (/^-?\d+$/.test(normalized) && normalized.length > 20) {
    try {
      return BigInt(normalized);
    } catch (_error) {
      return 0n;
    }
  }

  try {
    return parseUnits(normalized as `${number}`, USD_DECIMALS);
  } catch (_error) {
    try {
      return BigInt(normalized);
    } catch (_error) {
      return 0n;
    }
  }
}

// Keeping it for potential use or reference, but not used in the main card now
export function GeneralPerformanceDetailsRow({ row }: { row: PnlSummaryPoint }) {
  const { _ } = useLingui();

  return (
    <div className="flex justify-between border-b border-gray-lighter/10 py-12 last:border-0">
      <div className="text-gray-lighter">{_(bucketLabelMap[row.bucketLabel as keyof typeof bucketLabelMap])}</div>
      <div className="flex gap-16">
        <div>{formatUsd(row.volume, { maxThreshold: null })}</div>
        <div className={cx(getPositiveOrNegativeClass(row.pnlUsd))}>{formatUsd(row.pnlUsd, { displayPlus: true })}</div>
      </div>
    </div>
  );
}

type CustomCardRowProps = {
  label: ReactNode;
  value: ReactNode;
};

const CustomCardRow = (p: CustomCardRowProps) => {
  return (
    <CardRow
      classNameLabel="!font-medium !text-white text-[12px] sm:text-[16px] leading-[1]"
      classNameValue="!font-medium text-white text-[12px] sm:text-[16px] leading-[1]"
      label={p.label}
      value={p.value}
    />
  );
};
